import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock, Calendar, ChatCircle, House, X } from '@phosphor-icons/react';
import { Badge } from './ui/badge';

const CAREGIVER_TABS = [
  { path: '/clock', label: 'Clock', icon: Clock },
  { path: '/schedule', label: 'Schedule', icon: Calendar },
  { path: '/dashboard', label: 'Home', icon: House },
  { path: '/messages', label: 'Messages', icon: ChatCircle, showBadge: true },
];

// Bottom tab nav for caregivers on mobile
export const CaregiverBottomNav = ({ user, unreadCount }) => {
  const location = useLocation();
  if (!user || user.role !== 'caregiver') return null;
  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border pb-[env(safe-area-inset-bottom)]"
      data-testid="caregiver-bottom-nav"
    >
      <div className="flex items-center justify-around">
        {CAREGIVER_TABS.map((tab) => {
          const active = location.pathname === tab.path;
          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={`relative flex-1 flex flex-col items-center justify-center gap-1 py-2.5 transition-colors ${
                active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`bottom-nav-${tab.label.toLowerCase()}`}
            >
              <tab.icon weight={active ? 'fill' : 'duotone'} className="w-6 h-6" />
              <span className="text-[11px] font-medium">{tab.label}</span>
              {tab.showBadge && unreadCount > 0 && (
                <Badge className="absolute top-1 right-4 h-4 min-w-[1rem] px-1 text-[10px] bg-destructive text-destructive-foreground rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// PWA install prompt — shows a bottom banner inviting the user to install
export const InstallAppPrompt = () => {
  const [installEvent, setInstallEvent] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed as PWA? Don't show.
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;
    if (localStorage.getItem('pwa_install_dismissed') === '1') return;

    // Android / desktop — capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      setTimeout(() => setVisible(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari has no beforeinstallprompt — show manual instructions
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua);
    if (isIOS && isSafari) {
      setIsIos(true);
      setTimeout(() => setVisible(true), 2000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem('pwa_install_dismissed', '1');
    setDismissed(true);
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') {
      setVisible(false);
    }
    setInstallEvent(null);
  };

  if (!visible || dismissed) return null;

  return (
    <div
      className="md:hidden fixed bottom-[calc(env(safe-area-inset-bottom)+64px)] left-2 right-2 z-50 bg-primary text-primary-foreground rounded-2xl shadow-xl border border-primary-foreground/10 p-3 flex items-center gap-3"
      data-testid="install-app-prompt"
    >
      <div className="flex-1 text-xs">
        <p className="font-semibold">Install CareGrid on your phone</p>
        {isIos ? (
          <p className="opacity-90 mt-0.5">Tap <strong>Share</strong> → <strong>Add to Home Screen</strong></p>
        ) : (
          <p className="opacity-90 mt-0.5">Fast access & offline schedule viewing</p>
        )}
      </div>
      {!isIos && installEvent && (
        <button
          onClick={install}
          className="bg-background text-foreground rounded-xl px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
          data-testid="install-app-btn"
        >
          Install
        </button>
      )}
      <button onClick={dismiss} className="opacity-80 hover:opacity-100 p-1" data-testid="install-dismiss-btn">
        <X weight="bold" className="w-4 h-4" />
      </button>
    </div>
  );
};
